import {Component, ConfigOptions} from 'panel';
import get from 'lodash/get';

import {Workspace} from 'app_api/me/types';
import userDetailsStore from 'common/report/stores/user-details';
import {closestReportLink, getProjectID, getReportUrl, isVolt} from 'common/report/util';
import {spaNavManager} from 'common/report/util/spa-nav-manager';
import {createOrgProjects} from 'common/report/queries/app-api/legacy/orgs-settings-client';
import WorkspacesClient from 'common/report/queries/app-api/workspaces-client';
import {events, properties, track} from 'common/report/util/settings-tracking';
import {capitalize, truncateMiddle, pluralize} from 'common/util';
import {keyEventSwitcher} from 'common/util/dom';
import {isFestiveMode, setFestiveMode} from 'common/util/festive-mode';
import {registerMPElement} from 'common/util/register-element';
import {nameToInitials} from 'common/util/string';
import {promptManageWorkspaceModal} from 'common/util/workspaces';
import {Section} from 'common/components/items-menu/types';

import * as SearchTracking from './omnisearch/tracking';
import {AppInfo, ChromeHeaderState, NavCategories, OrgInfo, ProjectInfo, Toast} from './types';

import template from './index.jade';
import css from './index.styl?inline';

import './omnisearch';
import './reports-menu';
import './festive-mode-header-anim';

const PROJECT_TYPE_PROJECT = 1;
const PROJECT_TYPE_ROLLUP = 3;

const TOAST_TTL = 7000;
let toastId = 1;
const toastCtaCallbackMap = new Map<string, Function>();

const SETTINGS_CATEGORY_TRACKING = {
  Org: events.viewOrg,
  Account: events.viewAccountSettings,
  Project: events.viewProjectSettings,
  Workspace: events.viewWorkspaceSettings,
};

export class ChromeHeader extends Component<ChromeHeaderState> {
  static get attrsSchema() {
    return {
      ...super.attrsSchema,
      'allow-smart-hub': `boolean`,
      'current-report': `string`,
      hidden: `boolean`,
      'is-at-project-max': `boolean`,
      'is-persistable-report': `boolean`,
      'max-custom-apps-exceeded': `json`,
      'org-data': `json`,
      'platform-reports': `json`,
      'project-feature-flags': `json`,
      'project-id': `number`,
      'project-role': `string`,
      projects: `json`,
      'restricted-reports': `json`,
      'user-flags': `json`,
      'user-is-staff': `boolean`,
      'user-name': `string`,
      'user-whitelists': `json`,
      'utc-offset': `number`,
      'workspace-id': `number`,
      workspaces: `json`,
    };
  }

  get config(): ConfigOptions<ChromeHeaderState> {
    return {
      css,
      template,
      useShadowDom: true,
      defaultState: {
        allowSmartHub: true,
        applicationsMenuOpen: false,
        categories: {},
        createProjectError: null,
        createProjectModalOpen: false,
        createProjectName: null,
        createProjectSelectedOrgId: null,
        currentCategoryName: ``,
        currentProject: null,
        currentReportName: ``,
        errorAlertMsg: null,
        inviteModalOrgs: [],
        isAtProjectMax: false,
        isInviteModalOpen: false,
        omnisearchOpen: false,
        orgData: [],
        platformReports: [],
        projectFeatureFlags: new Set(),
        projectId: null,
        projectMenuOpen: false,
        projectMenuWorkspaces: [],
        projectRole: null,
        projects: [],
        restrictedReports: new Set(),
        selectedCategory: ``,
        setupMenuOpen: false,
        showNewProjectInput: false,
        showProjectLimitUpsell: false,
        smartHubMenuOpen: false,
        toasts: [],
        userStore: userDetailsStore,
        userFlags: {},
        userMenuOpen: false,
        userWhitelists: new Set(),
        utcOffset: 0,
        workspaceId: null,
        workspaces: [],
      },
      helpers: {
        getReportUrl,
        isFestiveMode,

        allMenusClosed: () =>
          !this.state.setupMenuOpen &&
          !this.state.projectMenuOpen &&
          !this.state.applicationsMenuOpen &&
          !this.state.smartHubMenuOpen &&
          !this.state.userMenuOpen,

        getProjectItems: (projects: ProjectInfo[]) =>
          projects
            .map((proj) => {
              const workspaceCount = this.getWorkspacesForProject(proj.id).length;
              const workspaceOverrides =
                workspaceCount > 0
                  ? {
                      subLabel: `${workspaceCount} ${pluralize(`Workspace`, workspaceCount)}`,
                      subLabelTheme: `soft`,
                      hasCaret: workspaceCount,
                    }
                  : {};

              return {
                label: proj.name || `My new project`,
                subLabel: proj.type === PROJECT_TYPE_ROLLUP && this.state.orgData.length > 1 ? `rollup project` : ``,
                subLabelTheme: `strong`,
                projectId: proj.id,
                type: proj.type,
                ...workspaceOverrides,
              };
            })
            .sort((a, b) => a.label.localeCompare(b.label)),

        selectProjectMenuBackClick: () => this.update({projectMenuWorkspaces: []}),

        projectMenuSections: () => {
          const allProjects = this.state.projects || [];
          const projectMenuWorkspaces = this.state.projectMenuWorkspaces;
          let sections;

          // Even one workspace should see submenu due to the introduction of
          // Explore Workspaces button
          if (projectMenuWorkspaces.length) {
            sections = [
              {
                items: projectMenuWorkspaces.map((workspace) => ({
                  label: workspace.name,
                  id: workspace.id,
                  projectId: workspace.project_id,
                  isWorkspace: true,
                })),
              },
              {
                items: [
                  {
                    label: `Explore Workspaces`,
                    icon: `open`,
                    projectId: projectMenuWorkspaces[0].project_id,
                    href: `/report/${projectMenuWorkspaces[0].project_id}/workspaces`,
                  },
                ],
              },
            ];
          } else if (this.state.orgData.length > 1) {
            sections = this.state.orgData.map((org) => ({
              label: this.state.orgData.length > 1 ? org.name : null,
              items: this.helpers.getProjectItems(allProjects.filter((p) => p.organization_id === org.id)),
            }));
          } else {
            const projects = allProjects.filter((p) => p.type === PROJECT_TYPE_PROJECT);
            const rollupProjects = allProjects.filter((p) => p.type === PROJECT_TYPE_ROLLUP);
            const hasRollupProjects = rollupProjects.length > 0;
            sections = [
              {
                // Only explicitly label if there are rollup projects, otherwise its just a single list
                label: hasRollupProjects ? `Projects` : ``,
                items: this.helpers.getProjectItems(projects),
              },
            ];

            if (hasRollupProjects) {
              sections.push({
                label: `Rollup Projects`,
                items: this.helpers.getProjectItems(rollupProjects),
              });
            }
          }

          return sections;
        },

        getCurrentWorkspace: () =>
          this.attr(`workspace-id`) &&
          this.state.workspaces.find((workspace) => workspace.id === this.attr(`workspace-id`)),

        truncateName: (name: string) => truncateMiddle(name, 18),

        updateCurrentCategoryAndReportNameFromUrl: (url?: string) => {
          const {pathname: locationPath, hash: locationHash} = url ? new URL(url) : window.location;

          // go through every report and see if we have an endpoint match
          for (const [categoryName, category] of Object.entries(this.state.categories)) {
            for (const [reportName, report] of Object.entries(category.reports)) {
              const [endpointPath, endpointHash] = report.endpoint.split(`#`);

              // allow trailing hash in endpoint match
              const hasEndpointMatch =
                locationPath.endsWith(`/${endpointPath}`) || locationPath.endsWith(`/${endpointPath}/`);
              const hasHashMatch = !endpointHash || locationHash.startsWith(`#${endpointHash}`);
              if (hasEndpointMatch && hasHashMatch) {
                this.update({
                  currentCategoryName: categoryName,
                  currentReportName: reportName,
                });
                return;
              }
            }
          }

          // if endpoint isn't found in categories tree e.g dashboards, parse report name from the location url
          const reMatch = /^\/report\/(?:workspace\/)?\d+\/([\w-]+)/.exec(locationPath);
          if (reMatch) {
            this.update({
              currentCategoryName: ``,
              currentReportName: reMatch[1],
            });
          }
        },

        initializeSpaNavManager: () => {
          if (this.state.currentProject) {
            // multiple async entry points, use the one that's ready
            spaNavManager.initialize({
              projectId: String(this.state.projectId),
              projectName: this.state.currentProject.name,
              workspaceId: this.state.workspaceId,
            });
          }
        },

        navigate: (url, ev = null, fromAnchorlink = false) => {
          let openInNewTab = false;
          if (ev) {
            if (ev.ctrlKey || ev.metaKey) {
              openInNewTab = true; // mouse event
            } else if (
              ev.detail &&
              ev.detail.modifierKeys &&
              (ev.detail.modifierKeys.ctrl || ev.detail.modifierKeys.meta)
            ) {
              openInNewTab = true; // items-menu event
            }
          }

          if (openInNewTab) {
            // if anchor link, browser will do the right thing by opening in new tab anyway
            if (!fromAnchorlink) {
              window.open(url, `_blank`);
            }
          } else {
            if (spaNavManager.isNavigable(url)) {
              // this will be a fake page navigation by swapping the iron app js bundle and css
              // we'll ask the window's beforeunload whether we should ask the user to stay on the page
              let shouldNavigate = true;
              if (window.onbeforeunload instanceof Function) {
                const beforeUnloadEv = new CustomEvent(`beforeunload`, {cancelable: true});
                window.onbeforeunload(beforeUnloadEv);
                if (beforeUnloadEv.defaultPrevented) {
                  shouldNavigate = confirm(`Changes you made may not be saved. Leave this page?`);
                }
              }

              if (shouldNavigate) {
                spaNavManager.navigate(url, ev);
                this.helpers.updateCurrentCategoryAndReportNameFromUrl(window.location.origin + url);
              } else {
                // user said no, don't navigate
                ev && ev.preventDefault();
              }
            } else {
              // if an anchor link is clicked, let the browser do it's thing
              // otherwise onbeforeunload will fire twice if we also set location.href
              // see https://mixpanel.atlassian.net/browse/RNA-737
              if (!fromAnchorlink) {
                window.location.href = url;
              }

              const handleBeforeUnload = (beforeUnloadEv: BeforeUnloadEvent) => {
                window.removeEventListener(`beforeunload`, handleBeforeUnload);

                // ev.defaultPrevented == true means the user was prompted to stay or leave.
                // we have no way to know what their choice was
                // so don't do an optimistic report whiteout and select the new report tab
                // if user leaves, the right state will be shown anyway after the page loads, just a bit slower
                if (!beforeUnloadEv.defaultPrevented) {
                  // optimisitic update that the new page started loading instantly
                  // by showing whiteout loader spinny and selecting the new report in nav
                  this.helpers.updateCurrentCategoryAndReportNameFromUrl(window.location.origin + url);
                  document.querySelector(`.whiteout_loader`).classList.remove(`hidden`);
                }
              };

              window.addEventListener(`beforeunload`, handleBeforeUnload);
            }
          }
        },

        // update selected tab on browser back/forward
        // only relevant in cases when page does not refresh between report changes
        handleHistoryStateChange: (ev: Event) => {
          this.helpers.updateCurrentCategoryAndReportNameFromUrl();

          if (ev.type === `popstate`) {
            spaNavManager.navigate(location.pathname + location.hash, ev);
          }
        },

        selectProject: (ev) => {
          const {item} = ev.detail;
          const projectId = item.projectId;
          const projectDomain = this.state.projects.find((p) => p.id === projectId).domain;

          if (item.href) {
            this.helpers.navigate(item.href, ev);
            return;
          }

          if (item.hasCaret) {
            const projectMenuWorkspaces = this.getWorkspacesForProject(projectId);
            if (projectMenuWorkspaces.length) {
              this.update({projectMenuWorkspaces});
              return;
            }
          }

          const workspaceId = item.isWorkspace ? item.id : get(this.getWorkspacesForProject(projectId)[0], `id`);

          this.update({projectMenuOpen: false, projectMenuWorkspaces: []});

          let navigatePath = workspaceId ? `/report/workspace/${workspaceId}` : `/report/${projectId}/`;
          const curProjectId = this.state.projectId;
          const curWorkspaceId = this.state.workspaceId;
          const locationPath = window.location.pathname;

          // e.g if user is in insights, navigate to insights in new project
          if (
            this.attr(`is-persistable-report`) &&
            workspaceId &&
            new RegExp(`/workspace/${curWorkspaceId}(/|$)`).exec(locationPath)
          ) {
            navigatePath = locationPath.replace(String(curWorkspaceId), workspaceId) + location.search;
          } else if (
            (this.attr(`is-persistable-report`) || isVolt()) &&
            !workspaceId &&
            new RegExp(`/${curProjectId}(/|$)`).exec(locationPath)
          ) {
            navigatePath = locationPath.replace(String(curProjectId), projectId) + location.search;
          }

          if (workspaceId) {
            mixpanel.track(`Selected Workspace`, {
              'Selected Project ID': projectId,
              'Selected Workspace ID': workspaceId,
              'Selected Workspace Name': ev.detail.item.label,
              // track url pattern e.g /report/workspace/<wid>/insights. remove trailing slashes
              'Navigate Url Pattern': navigatePath.replace(workspaceId, `<wid>`).replace(/\/$/, ``),
            });
          } else {
            mixpanel.track(`Selected Project`, {
              'Selected Project ID': projectId,
              'Selected Project Name': ev.detail.item.label,
              'Selected Project Type': ev.detail.item.type === PROJECT_TYPE_ROLLUP ? `Rollup Project` : `Project`,
              // track url pattern e.g /report/<pid>/insights. remove trailing slashes
              'Navigate Url Pattern': navigatePath.replace(projectId, `<pid>`).replace(/\/$/, ``),
            });
          }

          // This logic only applies when we are explicitly switching projects and not report types,
          // so we keep it out of helpers.navigate to avoid an adding another argument to a complex
          // function.
          if (projectDomain && projectDomain !== window.location.hostname) {
            window.location.assign(`//` + projectDomain + navigatePath);
          } else {
            this.helpers.navigate(navigatePath, ev);
          }
        },

        clickedCreateProject: () =>
          this.update(
            this.state.orgData.length > 1
              ? {
                  createProjectModalOpen: true,
                  projectMenuOpen: false,
                }
              : {
                  showNewProjectInput: true,
                },
          ),

        selectOrg: (orgId) => this.update({createProjectSelectedOrgId: orgId}),

        updateCreateProjectName: (ev) => this.update({createProjectName: ev.target.value}),

        clickedCancel: () => this.update({createProjectModalOpen: false}),

        clickedCreate: async () => {
          try {
            const resp = await createOrgProjects(this.state.createProjectSelectedOrgId, [this.state.createProjectName]);
            this.update({projectMenuOpen: false});
            this.helpers.navigate(`/report/${resp[0].id}/`);
          } catch (error) {
            this.update({createProjectError: error});
          }
        },

        getOrgsWithCreateProjectPermission: () =>
          this.state.orgData.filter((org) => this.orgHasPerm(org, `create_project`)),

        orgHasPerm: (org, perm) => this.orgHasPerm(org, perm),

        showProjectLimitUpsell: () => {
          this.update({showProjectLimitUpsell: true});
        },

        userInitials: () => nameToInitials(this.state.userStore.value.name),

        getOrgs: ({permissions = null} = {}) =>
          this.state.orgData.filter(
            (o) => !permissions || permissions.every((p) => o.permissions && o.permissions.includes(p)),
          ),

        menuItemSelected: (ev) => {
          const {item} = ev.detail;
          if (item.href) {
            this.trackSettingsMenuItemSelected(item);
            this.helpers.navigate(item.href, ev);
          } else {
            switch (item.label) {
              case `Log Out`: {
                // Clear out LocalStorage persistence
                window.localStorage.removeItem(`mp_persistence`);
                mixpanel.reset();
                mixpanel.idmgmt.reset();
                window.location.href = `/logout`;
                break;
              }
              case `Invite Users...`: {
                const orgUrlMatch = /^\/settings\/org\/(\d+)/.exec(window.location.pathname);
                const orgId = parseInt(orgUrlMatch && orgUrlMatch[1], 10) || null;
                const orgs = this.helpers.getOrgs({permissions: [`add_user`]});
                const org = orgs.find((o) => o.id === orgId);
                // if org id is specified by url and that org has `add_user` permission, show
                // invite modal for that org; otherwise show modal for all orgs with `add_user`
                // (user will choose org in modal)
                const inviteModalOrgs = org ? [org] : orgs;
                if (inviteModalOrgs.length) {
                  this.update({
                    isInviteModalOpen: true,
                    inviteModalOrgs,
                  });
                }
              }
            }
          }
        },

        closeInviteModal: () => {
          this.update({isInviteModalOpen: false});
        },

        menuChanged: (menuState: string, menuType: string) => {
          if (menuState === `open`) {
            this.update({[`${menuType}MenuOpen`]: true});
          } else if (menuState === `closed`) {
            this.update({[`${menuType}MenuOpen`]: false});
          }
        },

        selectMenuChanged: (menuStateName: string, isOpen: boolean) => {
          if (menuStateName === `projectMenuOpen` && isOpen) {
            this.update({projectMenuWorkspaces: this.getWorkspacesForProject(this.state.projectId)});
          }
          this.update({[menuStateName]: isOpen});
        },

        selectCategory: (catName: string) => {
          this.update({
            applicationsMenuOpen: false,
            projectMenuOpen: false,
            selectedCategory: catName,
            setupMenuOpen: false,
            smartHubMenuOpen: false,
            userMenuOpen: false,
          });
        },

        toggleSelectedCategory: (catName: string) => {
          // open and close menu if header is clicked
          const selectedCategory = this.state.selectedCategory !== catName ? catName : ``;
          this.helpers.selectCategory(selectedCategory);
        },

        handleAlertChange: (ev) => {
          if (ev.detail.state === `closed`) {
            this.update({errorAlertMsg: null});
          }
        },

        handleNotify: (ev) => {
          const {message} = ev.detail;
          this.addToast(message);
        },

        handleError: (ev) => {
          const {error, message} = ev.detail;
          this.showError(message || error.message, error);
        },

        handleLinkClick: (ev: MouseEvent) => {
          const target = ev.target as HTMLElement;
          if (target && target.closest) {
            const anchorLink: HTMLAnchorElement = closestReportLink(target);
            // let browser handle open in new tab interaction for a(target=_blank)
            if (anchorLink && anchorLink.target !== `_blank`) {
              this.helpers.navigate(anchorLink.getAttribute(`href`), ev, /* fromAnchorlink */ true);
            }
          }
        },

        chromeHotKeySwitcher: keyEventSwitcher(
          {
            'Alt+KeyF': () => {
              SearchTracking.trackHotKey(this.state.projectId, this.state.omnisearchOpen ? `closed` : `opened`);
              this.helpers.handleOmnisearchToggle();
            },
          },
          {stopPropagation: true},
        ),

        handleOmnisearchClose: ({detail}) => {
          if (!detail || !detail.navClosure) {
            SearchTracking.trackClosed(this.state.projectId);
          }

          this.update({omnisearchOpen: false});
        },

        handleOmnisearchToggle: (ev?: any) => {
          if (this.state.omnisearchOpen) {
            SearchTracking.trackClosed(this.state.projectId);
          }
          if (ev) {
            SearchTracking.trackClick(this.state.projectId, this.state.omnisearchOpen ? `closed` : `opened`);
          }
          this.update({omnisearchOpen: !this.state.omnisearchOpen});
        },

        closeToast: (toast) => this.closeToast(toast),

        getApplicationsMenuSections: (): Section[] => {
          const {platformReports, restrictedReports, projectFeatureFlags} = this.state;
          const selectedWorkspace = (this.state.workspaces || []).find(
            (workspace) => workspace.id === this.state.workspaceId,
          );
          const projectId = getProjectID();

          const toSectionItem = (app: AppInfo) => ({
            label: app.name || ``,
            subLabel: [app.description, app.created_by ? `Created by ${app.created_by}.` : null]
              .filter(Boolean)
              .join(`. `),
            wrapSubLabel: true,
            href: app.url,
            ...(app.gated ? {icon: `lightning-bolt`, iconSide: `right` as const} : {}),
          });

          const appSections: Section[] = [
            {
              label: `Mixpanel Apps`,
              items: [
                {
                  official_name: `jql`,
                  name: `JQL`,
                  description: `Write powerful queries to analyze your data in Mixpanel.`,
                  url: getReportUrl(`jql-console`, {projectId, workspaceId: this.state.workspaceId}),
                  gated: restrictedReports.has(`jql`),
                  // JQL would allow users to bypass data filters, it should be hidden for workspaces
                  hidden:
                    projectFeatureFlags.has(`hide_report_jql_console`) ||
                    (selectedWorkspace && !selectedWorkspace.is_global),
                },
                {
                  official_name: `segmentation`,
                  name: `Segmentation`,
                  description: `Measure the actions that people take.`,
                  url: `/report/${projectId}/segmentation`,
                  // hide segmentation for new projects as of 15th Jan 2019
                  hidden: projectId > 1_894_797 || projectFeatureFlags.has(`hide_report_segmentation`),
                },
                {
                  official_name: `formulas`,
                  name: `Formulas`,
                  description: `Create formulas to understand ratios of events.`,
                  url: `/report/${projectId}/formulas`,
                  gated: restrictedReports.has(`formulas`),
                  // hide formulas for new projects of Aug 2nd 2019
                  hidden:
                    projectId > 2_020_000 ||
                    projectFeatureFlags.has(`hide_report_formulas`) ||
                    (selectedWorkspace && !selectedWorkspace.is_global),
                },
                {
                  official_name: `revenue`,
                  name: `Revenue`,
                  description: `Easily understand and analyze the revenue you make from individual customers.`,
                  url: selectedWorkspace
                    ? `/report/workspace/${selectedWorkspace.id}/revenue`
                    : `/report/${projectId}/revenue`,
                  gated: restrictedReports.has(`revenue`),
                  hidden:
                    projectFeatureFlags.has(`hide_report_revenue`) ||
                    (selectedWorkspace && !selectedWorkspace.is_global),
                },
              ]
                .filter((app) => !app.hidden)
                .map(toSectionItem),
            },
            {
              label: `Build a custom app`,
              items: [
                {
                  official_name: `build-an-app`,
                  description: `If you're a developer, utilize our powerful APIs to build applications that help you visualize your own data in unique ways.`,
                  url: `/report/${projectId}/mpplatform/new/#choose_template`,
                  gated: this.attr(`max-custom-apps-exceeded`),
                },
              ].map(toSectionItem),
            },
            {
              label: `Custom Apps`,
              items: platformReports.map(toSectionItem),
            },
          ];

          return appSections;
        },

        getSettingsMenuSections: () => {
          const organizations = this.state.orgData
            .map((org) => ({
              org,
              label: org.name,
              href: `/settings/org/${org.id}`,
              tracking: SETTINGS_CATEGORY_TRACKING.Org,
              isDisabled: !this.orgHasPerm(org, `view_organization_settings`),
              subLabel: this.orgHasPerm(org, `view_organization_settings`)
                ? null
                : `Your role does not allow you to view settings`,
            }))
            .sort((a, b) => {
              // alpha sort if both are same state
              if (a.isDisabled === b.isDisabled) {
                return a.label.localeCompare(b.label);
              }
              // disabled to the bottom, enabled at the top
              return a.isDisabled ? 1 : -1;
            });

          let projectSection = [];
          if (this.state.currentProject && this.state.currentProject.can_view_project_settings) {
            const projectId = this.state.currentProject.id;
            projectSection = [
              {
                label: `project settings`,
                items: [
                  {
                    label: this.state.currentProject.name,
                    href: `/settings/project/${projectId}`,
                    value: this.state.currentProject.name,
                    tracking: SETTINGS_CATEGORY_TRACKING.Project,
                  },
                  this.state.projectFeatureFlags.has(`workspaces`) &&
                  mp.report.globals.permissions.includes(`manage_workspaces`)
                    ? {
                        label: `Manage Workspaces`,
                        href: `/settings/project/${projectId}#workspaces`,
                        value: `Manage Workspaces`,
                        tracking: SETTINGS_CATEGORY_TRACKING.Workspace,
                      }
                    : null,
                  {
                    label: `Install Mixpanel`,
                    icon: `install`,
                    href: `/report/${projectId}/setup/quick`,
                    value: `Install Mixpanel`,
                  },
                  {
                    label: `Codeless Tracking`,
                    href: `/report/${projectId}/autotrack/`,
                    value: `Codeless Tracking`,
                  },
                ].filter(Boolean),
              },
            ];
          }
          const orgsLimit = projectSection.length > 0 ? 3 : 6;

          return [
            {
              items: [
                {
                  label: this.state.userStore.value.name,
                  icon: `profile`,
                  href: `/settings/account`,
                  value: `Profile & Preferences`,
                  tracking: SETTINGS_CATEGORY_TRACKING.Account,
                },
              ],
            },
            {
              label: `organization settings`,
              items: organizations,
              showLimit: organizations.length > orgsLimit ? orgsLimit - 1 : 0, // prevent-off by one showLimit
            },
            ...projectSection,
            this.helpers.getOrgs({permissions: [`add_user`]}).length
              ? {items: [{label: `Invite Users...`, icon: `invite`}]}
              : null,
            {
              items: [{label: `Log Out`}],
            },
          ].filter(Boolean);
        },

        // show welcome banner for uber (temporary POC)
        showUberPocWelcomeBanner: (featureFlag: string) => {
          const messageBanner = document.body.querySelector(`mp-report-message-banner`);

          let message = ``;
          if (featureFlag === `show_uber_welcome`) {
            // This is for the POC project.
            // TODO(thiaga): Remove this once we remove the POC project.
            message = [
              `Welcome, Uber! This project is using test data.`,
              `For questions and feedback, please reach out on`,
              `<a href='https://hangouts.google.com/group/K81WzT4MBw426kqW8' target='_blank'>GChat</a>`,
            ].join(` `);
          } else if (featureFlag === `show_uber_welcome_prod`) {
            // This is for the main project.
            // TODO(thiaga): Remove this after the first couple of months.
            message = [
              `Welcome, Uber! This project has data till December 10th.`,
              `For questions and feedback, please reach out on`,
              `<a href='https://slack.com/share/ISM7KPF8X/vB4panGvSerbl53tCB5pDGvo/enQtOTA1MjU3Nzk3MzAzLTAyZWNlNTc4MDBlN2MxNmNlZTBlNmI3ODcxNzIwNDdkMTczYTkxODczZWI0YmI1MzJlZGJiMDNlZDRkYjQ2NTg' target='_blank'>Slack</a>`,
            ].join(` `);
          }

          Object.entries({
            visible: true,
            closeable: true,
            persistent: true,
            message,
          }).forEach(([attr, val]) => messageBanner.setAttribute(attr, String(val)));
        },

        handleToastCtaAction: (ev) => {
          const selectedToastId = ev.target.dataset.id;
          if (selectedToastId && toastCtaCallbackMap.has(selectedToastId)) {
            toastCtaCallbackMap.get(selectedToastId).call(null);
            toastCtaCallbackMap.delete(selectedToastId);
          }
        },

        getUserHasCreateWorkspacePermission: () => {
          const projectId = this.state.projectMenuWorkspaces[0].project_id;
          const {projects, orgData} = this.state;
          const project = projects.find((proj) => proj.id === projectId);
          return (
            project &&
            this.orgHasPerm(
              orgData.find((org) => org.id === project.organization_id),
              `manage_workspaces`,
            )
          );
        },

        handleCreateWorkspaceBtnClick: async () => {
          // grab this now because the menu can collapse behind the modal and will 'reset' to the current project based on attrs
          const projectId = this.state.projectMenuWorkspaces[0].project_id;
          try {
            const {didSave, name, description} = await promptManageWorkspaceModal(
              `Create New Workspace`,
              {},
              projectId,
            );
            if (didSave) {
              const workspace = await WorkspacesClient.createWorkspace(projectId, {name, description});
              this.state.userStore.update({
                workspaces: {
                  ...this.state.userStore.value.workspaces,
                  [workspace.id]: {
                    id: workspace.id,
                    is_default: workspace.isDefault,
                    is_global: workspace.isGlobal,
                    project_id: workspace.projectId,
                    name: workspace.name,
                  },
                },
              });
              if (this.state.projectMenuWorkspaces[0] && this.state.projectMenuWorkspaces[0].project_id === projectId) {
                // the already computed and sorted project menu needs to be updated with the new workspace
                this.update({projectMenuWorkspaces: this.getWorkspacesForProject(projectId)});
              }
              this.addToast(
                `Workspace ${name} Created`,
                false,
                `Manage`,
                () =>
                  (window.location.href = `/settings/project/${workspace.projectId}#workspaces/detail/${workspace.id}`),
              );
            }
          } catch (err) {
            if (err.message && (err.message as string).includes(`already exists`)) {
              this.addToast(
                `Workspace already exists`,
                true,
                `Manage Workspaces`,
                () => (window.location.href = `/settings/project/${projectId}#workspaces`),
              );
            } else {
              this.showError(`Workspace creation failed. Please try again or contact support.`, err);
            }
          }
        },
      },
    };
  }

  handleUserDetails() {
    // update calculated project metadata based on global data
    const projectId = this.attr(`project-id`) || parseInt(getProjectID(), 10);
    const currentProject = userDetailsStore.value.projects[projectId];
    const projectRole = capitalize(this.attr(`project-role`) || (currentProject && currentProject.role.name) || `----`);

    const orgData: OrgInfo[] = Object.values(userDetailsStore.value.organizations);
    const orgsWithCreatePerms = orgData.filter((org) => this.orgHasPerm(org, `create_project`));
    const createProjectSelectedOrgId = orgsWithCreatePerms.length && orgsWithCreatePerms[0].id;

    this.update({
      orgData, // TODO read directly from userDetailsStore without state copy
      createProjectSelectedOrgId,

      currentProject,
      projectId,
      projectRole,
      projects: Object.values(userDetailsStore.value.projects),
      workspaces: Object.values(userDetailsStore.value.workspaces),
    });
    this.helpers.initializeSpaNavManager();
  }

  public connectedCallback() {
    super.connectedCallback();

    const projects = this.attr(`projects`);
    if (projects) {
      // TODO - revert once out of trouble
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      userDetailsStore.subscribe(() => {});
      // metadata from attrs
      userDetailsStore.update({
        isStaff: this.attr(`user-is-staff`),
        name: this.attr(`user-name`),
        organizations: (this.attr(`org-data`) || []).reduce((obj, org) => ({[org.id]: org, ...obj}), {}),
        projects: projects.reduce((obj, project) => ({[project.id]: project, ...obj}), {}),
        workspaces: this.attr(`workspaces`).reduce((obj, workspace) => ({[workspace.id]: workspace, ...obj}), {}),
      });
      // TODO - remove this once we are subscribed unconditionally
      this.handleUserDetails();
    } else {
      // TODO - move this out of conditional
      userDetailsStore.subscribe(() => this.handleUserDetails());
      // project metadata from api
      userDetailsStore.fetch();
      this.handleUserDetails();
    }

    const workspaceId = this.attr(`workspace-id`);
    const projectMenuWorkspaces = this.getWorkspacesForProject(this.attr(`project-id`));
    const restrictedReports = new Set<string>(this.attr(`restricted-reports`) || []);
    const projectFeatureFlags = new Set<string>(this.attr(`project-feature-flags`));
    const userFlags = this.attr(`user-flags`) || [];
    const userWhitelists = new Set<string>(this.attr(`user-whitelists`) || []);
    const isProjectOrGlobalWorkspace = this.isProjectOrGlobalWorkspace(workspaceId, projectMenuWorkspaces);
    const categories = this.getCategories(
      projectFeatureFlags,
      restrictedReports,
      userWhitelists,
      isProjectOrGlobalWorkspace,
    );

    const platformReports = this.attr(`platform-reports`) || [];
    const allowSmartHub = this.attr(`allow-smart-hub`);
    const isAtProjectMax = this.attr(`is-at-project-max`);
    const utcOffset = this.attr(`utc-offset`) || null;
    const isFestiveMode = userWhitelists.has(`festive-mode-stage`) && projectFeatureFlags.has(`festive_mode_stage`);
    setFestiveMode(isFestiveMode);

    this.update({
      allowSmartHub,
      categories,
      currentReportName: this.attr(`current-report`),
      isAtProjectMax,
      platformReports,
      projectFeatureFlags,
      projectMenuWorkspaces,
      restrictedReports,
      userFlags,
      userWhitelists,
      utcOffset,
      workspaceId,
    });

    this.helpers.updateCurrentCategoryAndReportNameFromUrl();

    window.addEventListener(`popstate`, this.helpers.handleHistoryStateChange);
    window.addEventListener(`pushstate`, this.helpers.handleHistoryStateChange);
    window.addEventListener(`replacestate`, this.helpers.handleHistoryStateChange);
    // mp.globals is initialized later, initialize spaNavManager after DOMContentLoaded
    window.addEventListener(`DOMContentLoaded`, this.helpers.initializeSpaNavManager);
    document.addEventListener(`click`, this.helpers.handleLinkClick);
    this.el.addEventListener(`click`, this.helpers.handleLinkClick); // catch link clicks within shadow dom

    document.addEventListener(`keypress`, this.helpers.chromeHotKeySwitcher);

    if (projectFeatureFlags.has(`show_uber_welcome`)) {
      window.addEventListener(`DOMContentLoaded`, () => this.helpers.showUberPocWelcomeBanner(`show_uber_welcome`));
    } else if (projectFeatureFlags.has(`show_uber_welcome_prod`)) {
      window.addEventListener(`DOMContentLoaded`, () =>
        this.helpers.showUberPocWelcomeBanner(`show_uber_welcome_prod`),
      );
    }
  }

  public disconnectedCallback() {
    super.disconnectedCallback();

    window.removeEventListener(`popstate`, this.helpers.handleHistoryStateChange);
    window.removeEventListener(`pushstate`, this.helpers.handleHistoryStateChange);
    window.removeEventListener(`replacestate`, this.helpers.handleHistoryStateChange);
    window.removeEventListener(`DOMContentLoaded`, this.helpers.initializeSpaNavManager);
    document.removeEventListener(`click`, this.helpers.handleLinkClick);
    this.el.removeEventListener(`click`, this.helpers.handleLinkClick);
    document.removeEventListener(`keypress`, this.helpers.chromeHotKeySwitcher);
  }

  private getCategories(
    projectFeatureFlags: Set<string>,
    restrictedReports: Set<string>,
    userWhitelists: Set<string>,
    isProjectOrGlobalWorkspace: boolean,
  ): NavCategories {
    const categories: NavCategories = {
      analysis: {
        icon: `brand-analysis`,
        label: `Analysis`,
        twoColumn: true,
        reports: {
          insights: {
            description: `Deeply analyze your users and their actions`,
            endpoint: `insights`,
            icon: `brand-insights`,
            label: `Insights`,
          },
          flows: {
            description: `Explore user paths in your product`,
            icon: `brand-flows`,
            label: `Flows`,
            endpoint: `flows`,
            // user whitlisted flags are not gated
            gated: restrictedReports.has(`flows`) && !userWhitelists.has(`flows-2018`),
          },
          funnels: {
            description: `Pinpoint where and why users drop off`,
            icon: `brand-funnels`,
            label: `Funnels`,
            endpoint: `funnels`,
          },
          retention: {
            description: `Identify if users come back over time`,
            icon: `brand-retention`,
            label: `Retention`,
            endpoint: `retention`,
          },
          predict2: {
            description: `See which users are likely to convert`,
            icon: `brand-predict`,
            label: `Predict`,
            endpoint: `predict`,
            gated: restrictedReports.has(`predict`),
            hidden: !isProjectOrGlobalWorkspace,
          },
          signal: {
            description: `Identify actions that drive users to convert/retain`,
            icon: `brand-signal`,
            label: `Signal`,
            endpoint: `signal`,
            gated: restrictedReports.has(`signal`),
            hidden: !isProjectOrGlobalWorkspace,
          },
          launch_analysis: {
            description: `Measure the impact of new features and campaigns`,
            icon: `brand-impact`,
            label: `Impact`,
            endpoint: `impact`,
          },
          experiment_reporting: {
            beta: true,
            description: `Compare the impact of two sampled variants`,
            icon: `brand-experiment-reporting`,
            label: `Experiments`,
            endpoint: `experiment-reporting`,
          },
          live: {
            description: `View a live streaming feed of events`,
            endpoint: `live`,
            icon: `brand-liveview`,
            label: `Live view`,
          },
        },
      },

      users: {
        icon: `brand-user-groups`,
        label: `Users`,
        reports: {
          explore: {
            description: `Understand each user`,
            icon: `brand-explore`,
            label: `Explore`,
            endpoint: `explore`,
          },
          cohorts: {
            description: `Create groups of users to analyze across our reports`,
            icon: `brand-cohorts`,
            label: `Cohorts`,
            endpoint: `cohorts`,
            gated: restrictedReports.has(`cohorts`),
          },
        },
      },

      action: {
        icon: `brand-take-action`,
        label: `Messages & Experiments`,
        hidden: projectFeatureFlags.has(`hide_menu_messages`),
        reports: {
          campaign: {
            description: `Create and send a series of targeted messages`,
            endpoint: `triggers/campaign`,
            icon: `brand-campaign`,
            label: `Campaign`,
            gated: restrictedReports.has(`campaign`),
            // stop showing "campaign" tab to new projects unless they have explicitly asked for it
            hidden:
              (this.attr(`project-id`) > 1_984_369 && !projectFeatureFlags.has(`notifs_campaigns`)) ||
              !isProjectOrGlobalWorkspace,
          },
          messages: {
            description: `Re-engage customers with targeted messages`,
            endpoint: `triggers/messages`,
            icon: `brand-messages`,
            label: `Messages`,
            gated: restrictedReports.has(`messages`),
          },
          visual_journeys: {
            beta: true,
            description: `Plan and execute user journeys`,
            endpoint: `journeys`,
            icon: `brand-campaign`,
            label: `Journeys`,
            gated: restrictedReports.has(`visual_journeys`),
            hidden: !projectFeatureFlags.has(`notifs_visual_journeys`),
          },
          ab_test: {
            description: `Experiment to find the optimal user experiences`,
            endpoint: `ab_test`,
            icon: `brand-abtest`,
            label: `A/B testing`,
            gated: restrictedReports.has(`ab_testing`),
          },
        },
      },

      dataManagement: {
        icon: `brand-data-management`,
        label: `Data Management`,
        reports: {
          lexicon: {
            description: `Document and transform your events and properties`,
            icon: `brand-lexicon`,
            label: `Lexicon`,
            endpoint: `lexicon#transformations`,
          },
          audit: {
            description: `View possible issues with your implementation`,
            icon: `brand-audit`,
            label: `Data Audit`,
            beta: true,
            endpoint: `lexicon#audit`,
            hidden: !isProjectOrGlobalWorkspace,
          },
          integrations: {
            description: `Empower your team to act on insights with the tools they already love`,
            icon: `brand-integrations`,
            label: `Integrations`,
            endpoint: `integrations`,
            hidden: !isProjectOrGlobalWorkspace,
          },
        },
      },
    };

    // set name for each category and report based on its prop key and filter out hidden objects
    for (const [categoryName, category] of Object.entries(categories)) {
      category.name = categoryName;
      category.hidden && delete categories[categoryName];

      for (const [reportName, report] of Object.entries(category.reports)) {
        report.name = reportName;
        if (report.hidden) {
          delete category.reports[reportName];
        }
      }
    }

    return categories;
  }

  private getWorkspacesForProject(projectId: number | string): Workspace[] {
    // Ideally this would be a number, but we are going to be very careful because data types injected by django page
    //  render are not necessarily in-sync with our local types.
    const parsedProjectId = typeof projectId === `string` ? parseInt(projectId, 10) : projectId;
    return (Object.values(this.state.userStore.value.workspaces) || [])
      .filter((workspace: Workspace) => workspace.project_id === parsedProjectId)
      .sort(this.sortWorkspaces);
  }

  private isProjectOrGlobalWorkspace(workspaceId: number, projectMenuWorkspaces: Workspace[]) {
    if (projectMenuWorkspaces) {
      const workspace = projectMenuWorkspaces.find((ws) => ws.id === workspaceId);
      return !workspace || workspace.is_global;
    }
    return true;
  }

  private sortWorkspaces(a: Workspace, b: Workspace) {
    // Global Workspace should always be first
    // followed by Default Workspace
    // then finally in alphabetical order based on name
    if (a.is_global) {
      return -1;
    } else if (b.is_global) {
      return 1;
    } else if (a.is_default) {
      return -1;
    } else if (b.is_default) {
      return 1;
    } else {
      const aNormalizeName = a.name.toLocaleLowerCase();
      const bNormalizeName = b.name.toLocaleLowerCase();
      return aNormalizeName < bNormalizeName ? -1 : a === b ? 0 : 1;
    }
  }

  private orgHasPerm(org: OrgInfo, perm: string) {
    // Need this to prevent issues when swapping datatypes. Can remove after deploy
    if (Array.isArray(org.permissions)) {
      return org.permissions.includes(perm);
    } else if (typeof org.permissions === `object`) {
      return org.permissions[perm];
    } else {
      return false;
    }
  }

  private trackSettingsMenuItemSelected(item) {
    const {org, tracking} = item;
    if (tracking) {
      if (tracking === events.viewOrg && org) {
        track(tracking, {
          [properties.orgId]: org.id,
          [properties.orgName]: org.name,
        });
      } else {
        track(tracking);
      }
    }
  }

  private addToast(message: string, isError = false, ctaLabel = ``, ctaCallback: Function = null) {
    const newToast: Toast = {id: toastId.toString(), message, ttl: TOAST_TTL, 'is-error': isError, cta: ctaLabel};
    toastId++;
    if (ctaLabel && ctaCallback) {
      toastCtaCallbackMap.set(newToast.id, ctaCallback);
    }
    this.update({toasts: this.state.toasts.concat(newToast)});
    if (newToast.ttl) {
      newToast.timeout = setTimeout(() => this.closeToast(newToast), newToast.ttl);
    }
    return newToast.id;
  }

  private closeToast(toast: Toast) {
    clearTimeout(toast.timeout);
    this.update({
      toasts: this.state.toasts.map((t) => (t.id !== toast.id ? t : {...t, hide: true})),
    });
    toastCtaCallbackMap.delete(toast.id);
  }

  private showError(errorAlertMsg: string, err: Error) {
    if (err) {
      console.error(err);
    }
    this.update({errorAlertMsg});
  }
}

registerMPElement(`mp-chrome-header`, ChromeHeader);
